import { Button, OdinIcon } from '@myosh/odin-components';
import { type Ref, type RefObject, useCallback, useImperativeHandle, useRef } from 'react';
import Dialog, { type DialogRef } from './dialog';

interface AddItemDialogProps {
  onSubmit: () => void;
  errorTextRef: RefObject<HTMLParagraphElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  disabled?: boolean;
  ref?: Ref<DialogRef>;
}

const AddItemDialog = ({ onSubmit, errorTextRef, inputRef, disabled, ref }: AddItemDialogProps) => {
  const dialogRef = useRef<DialogRef>(null);

  const close = useCallback(() => {
    if (disabled) return;
    dialogRef.current?.close();
  }, []);

  useImperativeHandle(ref, () => ({
    close,
    open: () => dialogRef.current?.open(),
  }));

  return (
    <Dialog
      data-cy="add-item-dialog"
      onCancel={close}
      className="m-auto w-64 rounded-md bg-popover p-4 text-popover-foreground outline-hidden"
      ref={dialogRef}
    >
      <div className="flex flex-col gap-4">
        <div className="w-full">
          <div className="mb-2 flex flex-row-reverse">
            <button
              type="button"
              data-cy="add-item-dialog-close"
              title="Close"
              className="text-primary"
              onClick={close}
            >
              <OdinIcon icon="X" />
            </button>
          </div>
          <label htmlFor="add-new-item-input" className="flex self-start font-medium text-sm">
            Add new item
          </label>
          <input
            data-cy="add-item-dialog-input"
            disabled={disabled}
            placeholder="Enter new item"
            ref={inputRef}
            id="add-new-item-input"
            name="add-new-item-input"
            className="flex w-full flex-1 appearance-none rounded-sm border border-border px-2 py-1 text-sm focus:outline-hidden"
          />
          <p data-cy="add-item-dialog-error" className="hidden text-error text-xs" ref={errorTextRef} />
        </div>
        <Button data-cy="add-item-dialog-submit" disabled={disabled} onClick={onSubmit}>
          Submit
        </Button>
      </div>
    </Dialog>
  );
};

export default AddItemDialog;
